/**
 * A selectable category facet for the Rentals/Sales Finder. `value` is the full tag name the API returns on
 * RentalSalesResult.tags (so client-side filtering matches against it); `label` is the UI text. Same shape as
 * the FBO finder's FboFacetOption.
 */
export interface RentalSalesFacetOption {
  label: string;
  value: string;
}

/**
 * Aircraft Rental facets (Rental::*) — OR logic. Everything under Rental:: except Rental::Private (which
 * marks rentals as NOT available to the general public — see {@link RENTAL_PRIVATE_TAG}; a business carrying
 * only that tag is excluded from the finder server-side). Grouped by rental kind (airplane types, glider,
 * rotorcraft, equipment, simulators), matching the iOS "Aircraft and Equipment Rentals" card grouping.
 */
export const RENTAL_SALES_AIRCRAFT_RENTAL_OPTIONS: RentalSalesFacetOption[] = [
  { label: 'Airplane - Aerobatic', value: 'Rental::Airplane::Aerobatic' },
  { label: 'Airplane - Light Sport', value: 'Rental::Airplane::Light Sport' },
  { label: 'Airplane - Multi Engine', value: 'Rental::Airplane::Multi Engine' },
  { label: 'Airplane - Seaplane', value: 'Rental::Airplane::Seaplane' },
  { label: 'Airplane - Single Engine', value: 'Rental::Airplane::Single Engine' },
  { label: 'Airplane - Tailwheel', value: 'Rental::Airplane::Tailwheel' },
  { label: 'Airplane - Ultralight', value: 'Rental::Airplane::Ultralight' },
  { label: 'Glider', value: 'Rental::Glider' },
  { label: 'Rotorcraft - Gyroplane', value: 'Rental::Rotorcraft::Gyroplane' },
  { label: 'Rotorcraft - Helicopter', value: 'Rental::Rotorcraft::Helicopter' },
  { label: 'Equipment - Drone/UAS', value: 'Rental::Equipment::Drone/UAS' },
  { label: 'Equipment - Parachute', value: 'Rental::Equipment::Parachute' },
  { label: 'Equipment - Survival Gear', value: 'Rental::Equipment::Survival Gear' },
  { label: 'Simulator - Advanced Aviation Training Device (AATD)', value: 'Rental::Simulator::Advanced Aviation Training Device (AATD)' },
  { label: 'Simulator - Basic Aviation Training Device (BATD)', value: 'Rental::Simulator::Basic Aviation Training Device (BATD)' },
  { label: 'Simulator - Flight Training Device (FTD)', value: 'Rental::Simulator::Flight Training Device (FTD)' },
  { label: 'Simulator - Full Flight Simulator (FFS)', value: 'Rental::Simulator::Full Flight Simulator (FFS)' },
  { label: 'Simulator - Procedures Trainer', value: 'Rental::Simulator::Procedures Trainer' }
];

/**
 * Sales type facets (Sales::Types::*) — OR logic. The main options of the "Sales" section; the manufacturer
 * lists below are its nested subsections.
 */
export const RENTAL_SALES_SALES_TYPE_OPTIONS: RentalSalesFacetOption[] = [
  { label: 'New', value: 'Sales::Types::New' },
  { label: 'Used', value: 'Sales::Types::Used' },
  { label: 'Consignment', value: 'Sales::Types::Consignment' }
];

/**
 * Aircraft Manufacturers sales facets (Sales::Aircraft::*) — OR logic. Rendered as a nested subsection of
 * the "Sales" section (like the Service/Repair finder's Manufacturers subsections).
 */
export const RENTAL_SALES_AIRCRAFT_MANUFACTURER_OPTIONS: RentalSalesFacetOption[] = [
  { label: 'Aeroprakt', value: 'Sales::Aircraft::Aeroprakt' },
  { label: 'Air Tractor', value: 'Sales::Aircraft::Air Tractor' },
  { label: 'Airbus', value: 'Sales::Aircraft::Airbus' },
  { label: 'American Champion', value: 'Sales::Aircraft::American Champion' },
  { label: 'Aviat Aircraft', value: 'Sales::Aircraft::Aviat Aircraft' },
  { label: 'Beechcraft', value: 'Sales::Aircraft::Beechcraft' },
  { label: 'Bell', value: 'Sales::Aircraft::Bell' },
  { label: 'Bellanca', value: 'Sales::Aircraft::Bellanca' },
  { label: 'Boeing', value: 'Sales::Aircraft::Boeing' },
  { label: 'Bombardier', value: 'Sales::Aircraft::Bombardier' },
  { label: 'Cessna', value: 'Sales::Aircraft::Cessna' },
  { label: 'Cirrus', value: 'Sales::Aircraft::Cirrus' },
  { label: 'Dassault', value: 'Sales::Aircraft::Dassault' },
  { label: 'de Havilland', value: 'Sales::Aircraft::de Havilland' },
  { label: 'Diamond', value: 'Sales::Aircraft::Diamond' },
  { label: 'Embraer', value: 'Sales::Aircraft::Embraer' },
  { label: 'Eurocopter', value: 'Sales::Aircraft::Eurocopter' },
  { label: 'Flight Design', value: 'Sales::Aircraft::Flight Design' },
  { label: 'Grumman', value: 'Sales::Aircraft::Grumman' },
  { label: 'Gulfstream', value: 'Sales::Aircraft::Gulfstream' },
  { label: 'Hawker Beechcraft', value: 'Sales::Aircraft::Hawker Beechcraft' },
  { label: 'Honda Jet', value: 'Sales::Aircraft::Honda Jet' },
  { label: 'Luscombe', value: 'Sales::Aircraft::Luscombe' },
  { label: 'Maule', value: 'Sales::Aircraft::Maule' },
  { label: 'Mooney', value: 'Sales::Aircraft::Mooney' },
  { label: 'Piaggio', value: 'Sales::Aircraft::Piaggio' },
  { label: 'Pilatus', value: 'Sales::Aircraft::Pilatus' },
  { label: 'Piper', value: 'Sales::Aircraft::Piper' },
  { label: 'Pipistrel', value: 'Sales::Aircraft::Pipistrel' },
  { label: 'Robinson', value: 'Sales::Aircraft::Robinson' },
  { label: 'Sikorsky', value: 'Sales::Aircraft::Sikorsky' },
  { label: 'Socata', value: 'Sales::Aircraft::Socata' },
  { label: 'Stearman', value: 'Sales::Aircraft::Stearman' },
  { label: 'Tecnam', value: 'Sales::Aircraft::Tecnam' },
  { label: 'Vans', value: 'Sales::Aircraft::Vans' },
  { label: 'Waco', value: 'Sales::Aircraft::Waco' }
];

/**
 * Avionics Manufacturers sales facets (Sales::Avionics::*) — OR logic. Rendered as a nested subsection of
 * the "Sales" section.
 */
export const RENTAL_SALES_AVIONICS_MANUFACTURER_OPTIONS: RentalSalesFacetOption[] = [
  { label: 'Advanced Flight Systems', value: 'Sales::Avionics::Advanced Flight Systems' },
  { label: 'Aerosonic', value: 'Sales::Avionics::Aerosonic' },
  { label: 'Aircell', value: 'Sales::Avionics::Aircell' },
  { label: 'Appareo', value: 'Sales::Avionics::Appareo' },
  { label: 'Aspen', value: 'Sales::Avionics::Aspen' },
  { label: 'Avidyne', value: 'Sales::Avionics::Avidyne' },
  { label: 'Becker', value: 'Sales::Avionics::Becker' },
  { label: 'Bendix-King', value: 'Sales::Avionics::Bendix-King' },
  { label: 'Century', value: 'Sales::Avionics::Century' },
  { label: 'Cobham', value: 'Sales::Avionics::Cobham' },
  { label: 'Collins', value: 'Sales::Avionics::Collins' },
  { label: 'DAC International', value: 'Sales::Avionics::DAC International' },
  { label: 'Dynon', value: 'Sales::Avionics::Dynon' },
  { label: 'Electronics International', value: 'Sales::Avionics::Electronics International' },
  { label: 'Flight Display Systems', value: 'Sales::Avionics::Flight Display Systems' },
  { label: 'FreeFlight Systems', value: 'Sales::Avionics::FreeFlight Systems' },
  { label: 'Garmin', value: 'Sales::Avionics::Garmin' },
  { label: 'Genesys Aerosystems', value: 'Sales::Avionics::Genesys Aerosystems' },
  { label: 'Honeywell', value: 'Sales::Avionics::Honeywell' },
  { label: 'Icom', value: 'Sales::Avionics::Icom' },
  { label: 'Insight', value: 'Sales::Avionics::Insight' },
  { label: 'JP', value: 'Sales::Avionics::JP' },
  { label: 'KGB Aviation Solutions', value: 'Sales::Avionics::KGB Aviation Solutions' },
  { label: 'L-3', value: 'Sales::Avionics::L-3' },
  { label: 'Mid-Continent', value: 'Sales::Avionics::Mid-Continent' },
  { label: 'Narco', value: 'Sales::Avionics::Narco' },
  { label: 'PS Engineering', value: 'Sales::Avionics::PS Engineering' },
  { label: 'Rockwell Collins', value: 'Sales::Avionics::Rockwell Collins' },
  { label: 'S-TEC', value: 'Sales::Avionics::S-TEC' },
  { label: 'Sandel', value: 'Sales::Avionics::Sandel' },
  { label: 'Sandia Aerospace', value: 'Sales::Avionics::Sandia Aerospace' },
  { label: 'Shadin', value: 'Sales::Avionics::Shadin' },
  { label: 'Technisonic', value: 'Sales::Avionics::Technisonic' },
  { label: 'TKM', value: 'Sales::Avionics::TKM' },
  { label: 'Trig Avionics', value: 'Sales::Avionics::Trig Avionics' },
  { label: 'Trio Avionics', value: 'Sales::Avionics::Trio Avionics' },
  { label: 'TruTrack Flight Systems', value: 'Sales::Avionics::TruTrack Flight Systems' },
  { label: 'uAvionix', value: 'Sales::Avionics::uAvionix' },
  { label: 'Universal Avionics', value: 'Sales::Avionics::Universal Avionics' },
  { label: 'Vertex Standard', value: 'Sales::Avionics::Vertex Standard' }
];

/**
 * Filter state for the Rentals/Sales Finder. Holds the keyword plus the selected category facets. Selected
 * facets are stored as arrays of tag names so the OR filtering logic (see SearchPageComponent.filterRentalSales)
 * can match directly against RentalSalesResult.tags. Empty arrays mean "no constraint" (show all). Mirrors
 * {@link FboFinderFilter}.
 */
export class RentalSalesFinderFilter {

  /** Keyword: business name or airport ident. */
  name = '';

  /** Selected Aircraft Rental tag names (OR logic). */
  aircraftRental: string[] = [];

  /** Selected Sales type tag names (New/Used/Consignment; OR logic). */
  salesTypes: string[] = [];

  /** Selected Aircraft Manufacturers sales tag names (OR logic). */
  aircraftSalesManufacturers: string[] = [];

  /** Selected Avionics Manufacturers sales tag names (OR logic). */
  avionicsSalesManufacturers: string[] = [];
}
